import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import type { ProfilePage, RosterRow } from "@amber/application/contracts";
import type { PlanView } from "@amber/profiles/contracts";
import { Portrait, type LifecycleKind } from "@amber/profiles/ui";
import { AppLink, Status } from "@amber/ui";
import type { ProfilePageState, ProfilePanel } from "./pageState";
import { ProfileActions } from "./ProfileActions";
import { toneOf } from "./rosterFilters";

function KeyNumber({ label, children, href }: { label: string; children: ReactNode; href?: string }) {
  const body = (
    <>
      <span className="block text-md text-muted">{label}</span>
      <span className="font-mono text-base font-semibold text-fg">{children}</span>
    </>
  );
  return (
    <div className="min-w-[120px] border-r border-divider px-3 py-1.5 last:border-r-0">
      {href ? (
        <AppLink href={href} className="block no-underline">
          {body}
        </AppLink>
      ) : (
        body
      )}
    </div>
  );
}

export function ProfileHeader({
  p,
  plan,
  state,
  editor,
  row,
  appearances,
  onPanel,
  onLifecycle,
}: {
  p: ProfilePage;
  plan: PlanView | null;
  state: ProfilePageState;
  editor: boolean;
  row: RosterRow | undefined;
  appearances: number | undefined;
  onPanel: (panel: ProfilePanel) => void;
  onLifecycle: (kind: LifecycleKind) => void;
}) {
  return (
    <header className="px-4 pt-3">
      <div className="flex items-center gap-2 text-md">
        <AppLink href="/profiles" className="inline-flex items-center gap-1 font-strong">
          <ArrowLeft size={13} aria-hidden="true" /> Profiles
        </AppLink>
        <span className="text-muted">Profile</span>
        <ProfileActions p={p} state={state} editor={editor} onPanel={onPanel} onLifecycle={onLifecycle} />
      </div>
      <div className="mt-2 flex flex-wrap items-start gap-4">
        <Portrait name={p.name} url={p.identity.picture?.url ?? null} size="lg" />
        <div className="min-w-0 flex-1">
          <h1 id="view-title" className="m-0 text-2xl font-semibold">
            {p.name}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-ui">
            <Status tone={toneOf(p.lifecycle_state)}>
              {p.state_label}
              {state.draftLike ? " · not monitoring" : ""}
            </Status>
            {plan?.pending ? <span className="font-strong text-waiting">Changes pending</span> : null}
          </div>
          <p className="m-0 mt-1 text-md text-muted">
            {[
              p.role,
              p.affiliation,
              p.languages.length > 0 ? `Languages ${p.languages.map((l) => l.toUpperCase()).join(", ")}` : null,
              p.owner ? `Owner ${p.owner}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <p className="m-0 mt-0.5 text-md text-muted">{state.planLine}</p>
        </div>
        <div className="flex rounded-xl border border-divider">
          <KeyNumber label="Sources" href={`/profiles/${p.id}?tab=sources`}>
            {row?.sources ?? "—"}
            {row?.worst_health && row.worst_health.state !== "healthy" ? (
              <span className="font-sans text-md font-normal text-muted">
                {" · "}
                {row.worst_health.count} {row.worst_health.label.toLowerCase()}
              </span>
            ) : null}
          </KeyNumber>
          <KeyNumber label="Open gaps" {...(row ? { href: row.open_gaps.href } : {})}>
            {row?.open_gaps.count ?? "—"}
          </KeyNumber>
          <KeyNumber label="Appears as speaker in" href={`/profiles/${p.id}?tab=items`}>
            {appearances ?? "—"} <span className="font-sans text-md font-normal text-muted">other items</span>
          </KeyNumber>
        </div>
      </div>
    </header>
  );
}
